import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SparklesIcon, ArrowLeftIcon, MicrophoneIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../../stores/useAuthStore';
import { useProductosStore } from '../../../stores/useProductosStore';
import { useEstudioDisenoStore } from '../../../stores/useEstudioDisenoStore';
import { LIMITE_CORTESIA_IA } from '../../../utils/estudioDiseno/types';
import { usadasDelMes } from '../../../utils/estudioDiseno/cuota';
import { loadImageFromFile } from '../../../utils/estudioDiseno/loadImage';
import { imageToJpegBase64 } from '../../../utils/estudioDiseno/imageBase64';
import { generarImagenEstudio } from '../../../utils/estudioDiseno/generarIa';
import { resolverRutaIa } from '../../../utils/estudioDiseno/proveedorIa';
import { agregarImagenRedes } from '../../../stores/tusRedesStore';
import TusRedesPanel from './TusRedesPanel';
import type { ProductoParaRedes } from './TusRedesEditor';

type SeccionId = 'imagenes' | 'redes';

export default function EstudioDisenoPage() {
  const { currentEmpresa } = useAuthStore();
  const empresaId = currentEmpresa?.id || '';
  const { productos, fetchProductos, uploadProductImages, crearProducto } = useProductosStore();
  const { activos, uso, iaConfig, isLoading, error, fetchAll } = useEstudioDisenoStore();

  const [seccion, setSeccion] = useState<SeccionId>('imagenes');
  const [fotoPlantilla, setFotoPlantilla] = useState<string | null>(null);

  useEffect(() => {
    if (!empresaId) return;
    void fetchAll(empresaId);
    void fetchProductos(empresaId);
  }, [empresaId, fetchAll, fetchProductos]);

  const usadas = usadasDelMes(uso?.generaciones_cortesia ?? 0, uso?.periodo);
  const limite = uso?.limite_cortesia && uso.limite_cortesia >= LIMITE_CORTESIA_IA ? uso.limite_cortesia : LIMITE_CORTESIA_IA;
  const rutaIa = resolverRutaIa({
    preferido: iaConfig?.proveedor_preferido,
    usadas,
    limite,
    hasOpenaiKey: Boolean(iaConfig?.has_openai_key),
    hasGeminiKey: Boolean(iaConfig?.has_gemini_key),
  });

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="estudio-diseno-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <Link to="/productos" className="inline-flex items-center text-sm text-blue-700 hover:underline mb-1">
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Volver a productos
          </Link>
          <h1 className="text-2xl font-bold" style={{ color: '#0B1F4A' }}>
            Centro de Diseño
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Sube la foto del producto y escribe el prompt. ChatGPT genera la imagen. Clientelo paga 200
            al mes, GPT Image 2.5, calidad media. Con tu propia clave o ChatGPT no hay tope nuestro, solo
            el de tu proveedor.
          </p>
        </div>
        <div className="text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <SparklesIcon className="w-4 h-4 inline mr-1 text-[#1A56DB]" />
          {rutaIa.estado}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => setSeccion('imagenes')}
          className={`min-h-[44px] px-4 rounded-full text-sm font-semibold ${
            seccion === 'imagenes' ? 'bg-[#0B1F4A] text-white' : 'bg-white border border-gray-300 text-gray-700'
          }`}
        >
          Imágenes
        </button>
        <button
          type="button"
          data-testid="tus-redes-tab"
          onClick={() => setSeccion('redes')}
          className={`min-h-[44px] px-4 rounded-full text-sm font-semibold ${
            seccion === 'redes' ? 'bg-[#0B1F4A] text-white' : 'bg-white border border-gray-300 text-gray-700'
          }`}
        >
          Tus Redes
        </button>
      </div>

      {seccion === 'redes' && currentEmpresa && (
        <TusRedesPanel
          empresa={currentEmpresa}
          productos={productos}
          imagenesEstudio={activos.map((a) => ({ id: a.id, nombre: a.nombre, imagen_url: a.imagen_url, tipo: a.tipo }))}
          fotoLista={fotoPlantilla}
        />
      )}

      {seccion === 'imagenes' && (
        <GenerarImagenPanel
          empresaId={empresaId}
          productos={productos}
          cargando={isLoading}
          errorCarga={error}
          onListo={() => {
            if (empresaId) void fetchAll(empresaId);
          }}
          guardarEnProducto={uploadProductImages}
          crearProducto={(nombre, precio, file) => crearConFoto(empresaId, nombre, precio, file, crearProducto, uploadProductImages)}
          onUsarPlantilla={async (file) => {
            const dataUrl = await archivoADataUrl(file);
            if (empresaId) await agregarImagenRedes(empresaId, file, 'Imagen generada');
            setFotoPlantilla(dataUrl);
            setSeccion('redes');
          }}
        />
      )}
    </div>
  );
}

function archivoADataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('No se pudo leer la imagen'));
    reader.readAsDataURL(file);
  });
}

async function crearConFoto(
  empresaId: string,
  nombre: string,
  precio: number,
  file: File,
  crearProducto: (producto: { nombre: string; precioVenta: number; categoria: string; stock: number }, empresaId: string) => Promise<{ id: string } | null>,
  guardarEnProducto: (productoId: string, files: File[]) => Promise<string[]>
): Promise<void> {
  const creado = await crearProducto(
    { nombre, precioVenta: precio, categoria: '', stock: 0 },
    empresaId
  );
  if (!creado?.id) throw new Error('No se pudo crear el producto');
  await guardarEnProducto(creado.id, [file]);
}

function textoBusqueda(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('es');
}

function GenerarImagenPanel({
  empresaId,
  productos,
  cargando,
  errorCarga,
  onListo,
  guardarEnProducto,
  crearProducto,
  onUsarPlantilla,
}: {
  empresaId: string;
  productos: ProductoParaRedes[];
  cargando: boolean;
  errorCarga: string | null;
  onListo: () => void;
  guardarEnProducto: (productoId: string, files: File[]) => Promise<string[]>;
  crearProducto: (nombre: string, precio: number, file: File) => Promise<void>;
  onUsarPlantilla: (file: File) => Promise<void>;
}) {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [resultado, setResultado] = useState<string | null>(null);
  const [resultadoFile, setResultadoFile] = useState<File | null>(null);
  const [nombreDescarga, setNombreDescarga] = useState('imagen.jpg');
  const [busy, setBusy] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [destino, setDestino] = useState<'producto' | 'nuevo' | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [precioNuevo, setPrecioNuevo] = useState('');
  const [escuchando, setEscuchando] = useState(false);
  const reconocimiento = useRef<SpeechRecognition | null>(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'es-CO';
    r.continuous = false;
    r.interimResults = false;
    r.onresult = (e: SpeechRecognitionEvent) => {
      const texto = e.results[0]?.[0]?.transcript?.trim() || '';
      if (texto) {
        setPrompt((prev) => (prev.trim() ? `${prev.trim()} ${texto}` : texto));
      }
      setEscuchando(false);
    };
    r.onerror = () => setEscuchando(false);
    r.onend = () => setEscuchando(false);
    reconocimiento.current = r;
    return () => {
      try {
        reconocimiento.current?.abort();
      } catch {
        /* el navegador ya cerró el micrófono */
      }
    };
  }, []);

  const elegir = (file: File | null) => {
    setArchivo(file);
    setResultadoFile(null);
    setAviso(null);
    setDestino(null);
    setResultado((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return file ? URL.createObjectURL(file) : null;
    });
  };

  const dictar = () => {
    const r = reconocimiento.current;
    if (!r) {
      setError('Este navegador no permite dictar. Escríbelo, o abre Chrome.');
      return;
    }
    if (escuchando) {
      r.abort();
      setEscuchando(false);
      return;
    }
    setError(null);
    try {
      r.start();
      setEscuchando(true);
    } catch {
      setEscuchando(false);
      setError('No se pudo abrir el micrófono. Revisa el permiso del navegador.');
    }
  };

  const generar = async () => {
    if (!archivo || !prompt.trim() || !empresaId) return;
    if (escuchando) {
      reconocimiento.current?.abort();
      setEscuchando(false);
    }
    setBusy(true);
    setError(null);
    try {
      const img = await loadImageFromFile(archivo);
      const jpeg = await imageToJpegBase64(img);
      const result = await generarImagenEstudio({
        empresaId,
        prompt: prompt.trim(),
        images: [jpeg],
        provider: 'cortesia',
      });
      setResultado((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(result.file);
      });
      setNombreDescarga(result.file.name || 'imagen.jpg');
      setResultadoFile(result.file);
      setAviso(null);
      setDestino(null);
      onListo();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo generar la imagen');
    } finally {
      setBusy(false);
    }
  };

  const guardarFotoEn = async (productoId: string, nombre: string) => {
    if (!resultadoFile) return;
    setGuardando(true);
    setError(null);
    try {
      await guardarEnProducto(productoId, [resultadoFile]);
      setAviso(`Listo. La foto quedó en ${nombre}.`);
      setDestino(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar la foto');
    } finally {
      setGuardando(false);
    }
  };

  const crearProductoConFoto = async () => {
    if (!resultadoFile || !nombreNuevo.trim()) return;
    setGuardando(true);
    setError(null);
    try {
      const precio = Number(precioNuevo.replace(/\D/g, '')) || 0;
      await crearProducto(nombreNuevo.trim(), precio, resultadoFile);
      setAviso(`Listo. Creé ${nombreNuevo.trim()} con esta foto.`);
      setDestino(null);
      setNombreNuevo('');
      setPrecioNuevo('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear el producto');
    } finally {
      setGuardando(false);
    }
  };

  const usarEnPlantilla = async () => {
    if (!resultadoFile) return;
    setGuardando(true);
    setError(null);
    try {
      await onUsarPlantilla(resultadoFile);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo abrir la plantilla');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 max-w-xl space-y-5">
      {cargando && <p className="text-sm text-gray-500">Cargando…</p>}
      {errorCarga && <p className="text-sm text-red-700">{errorCarga}</p>}

      <label className="block">
        <span className="block text-sm font-semibold mb-2" style={{ color: '#0B1F4A' }}>
          Foto del producto
        </span>
        <input
          type="file"
          accept="image/*"
          className="block w-full text-sm"
          onChange={(e) => elegir(e.target.files?.[0] || null)}
        />
      </label>

      {preview && (
        <img src={preview} alt="Foto del producto" className="max-h-72 w-full object-contain rounded-lg border bg-gray-50" />
      )}

      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-sm font-semibold" style={{ color: '#0B1F4A' }}>
            Prompt
          </span>
          <button
            type="button"
            onClick={dictar}
            disabled={busy}
            aria-pressed={escuchando}
            className={`inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg text-sm font-semibold border ${
              escuchando
                ? 'bg-red-50 border-red-300 text-red-700'
                : 'bg-white border-gray-300 text-[#0B1F4A]'
            } disabled:opacity-50`}
          >
            <MicrophoneIcon className="w-5 h-5" />
            {escuchando ? 'Escuchando…' : 'Dictar'}
          </button>
        </div>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          placeholder="Escríbelo o tócalo con Dictar. Ejemplo: foto de catálogo, fondo blanco, luz suave"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <p className="mt-1 text-xs text-gray-500">Habla en español. El texto queda en el prompt y puedes editarlo.</p>
      </div>

      <button
        type="button"
        disabled={!archivo || !prompt.trim() || busy || !empresaId}
        onClick={() => void generar()}
        className="min-h-[44px] px-5 rounded-lg bg-[#1A56DB] text-white font-semibold disabled:opacity-50"
      >
        {busy ? 'Generando…' : 'Generar'}
      </button>

      {error && <p className="text-sm text-red-700">{error}</p>}

      {resultado && resultadoFile && (
        <div className="space-y-3">
          <img src={resultado} alt="Imagen generada" className="max-h-96 w-full object-contain rounded-lg border" />
          <div className="flex flex-wrap gap-2">
            <a
              href={resultado}
              download={nombreDescarga}
              className="inline-flex items-center min-h-[44px] px-4 rounded-lg border border-gray-300 font-semibold text-sm"
            >
              Descargar
            </a>
            <button
              type="button"
              disabled={guardando}
              onClick={() => setDestino((d) => (d === 'producto' ? null : 'producto'))}
              className="min-h-[44px] px-4 rounded-lg border border-gray-300 text-sm font-semibold"
            >
              Guardar en producto
            </button>
            <button
              type="button"
              disabled={guardando}
              onClick={() => setDestino((d) => (d === 'nuevo' ? null : 'nuevo'))}
              className="min-h-[44px] px-4 rounded-lg border border-gray-300 text-sm font-semibold"
            >
              Crear producto
            </button>
            <button
              type="button"
              disabled={guardando}
              onClick={() => void usarEnPlantilla()}
              className="min-h-[44px] px-4 rounded-lg bg-[#0B1F4A] text-white text-sm font-semibold disabled:opacity-50"
            >
              Usar en plantilla
            </button>
          </div>
          {destino === 'producto' && (
            <BuscarProducto
              productos={productos}
              busqueda={busqueda}
              onBusqueda={setBusqueda}
              disabled={guardando}
              onElegir={(id, nombre) => void guardarFotoEn(id, nombre)}
            />
          )}
          {destino === 'nuevo' && (
            <form
              className="space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                void crearProductoConFoto();
              }}
            >
              <input
                value={nombreNuevo}
                onChange={(e) => setNombreNuevo(e.target.value)}
                placeholder="Nombre del producto"
                className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[44px]"
              />
              <input
                inputMode="numeric"
                value={precioNuevo}
                onChange={(e) => setPrecioNuevo(e.target.value)}
                placeholder="Precio de venta"
                className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[44px]"
              />
              <button
                type="submit"
                disabled={guardando || !nombreNuevo.trim()}
                className="min-h-[44px] px-4 rounded-lg bg-[#1A56DB] text-white text-sm font-semibold disabled:opacity-50"
              >
                {guardando ? 'Creando…' : 'Crear con esta foto'}
              </button>
            </form>
          )}
          {aviso && <p className="text-sm text-green-800">{aviso}</p>}
        </div>
      )}
    </div>
  );
}

function BuscarProducto({
  productos,
  busqueda,
  onBusqueda,
  disabled,
  onElegir,
}: {
  productos: ProductoParaRedes[];
  busqueda: string;
  onBusqueda: (value: string) => void;
  disabled: boolean;
  onElegir: (id: string, nombre: string) => void;
}) {
  const q = textoBusqueda(busqueda.trim());
  const lista = productos
    .filter((p) => !q || textoBusqueda(p.nombre).includes(q))
    .slice(0, 8);
  return (
    <div className="space-y-2">
      <input
        type="search"
        value={busqueda}
        onChange={(e) => onBusqueda(e.target.value)}
        placeholder="Buscar producto por nombre"
        className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[44px]"
      />
      {lista.length === 0 && <p className="text-sm text-gray-500">No hay productos con ese nombre.</p>}
      <ul className="divide-y border border-gray-200 rounded-lg max-h-64 overflow-auto">
        {lista.map((p) => {
          const foto = p.imagenes?.[0] || p.imagen || '';
          return (
            <li key={p.id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onElegir(p.id, p.nombre)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left min-h-[44px] hover:bg-gray-50 disabled:opacity-50"
              >
                {foto ? (
                  <img src={foto} alt="" className="w-10 h-10 rounded object-cover bg-gray-100" />
                ) : (
                  <span className="w-10 h-10 rounded bg-gray-100" />
                )}
                <span className="text-sm font-medium text-gray-900">{p.nombre}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
